import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Navbar from "../../Header/header";
import SASidebar from "../../Dashboard/SuperAdmin/Superadmincom/SuperAdminSidebar";
import { API_LoginURL } from "../../../config";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";
import { ClipLoader } from "react-spinners";
import Select from 'react-select';
import { debounce } from 'lodash';

export default function SSPIQuestion() {
  // State management
  const [state, setState] = useState({
    sspiMasters: [],
    sspiQuestions: [],
    sspiExam:[],
    classList:[],
    loading: true,
    formSubmitting: false,
    formData: {
      sspiQuestionId: 0,
      sspiMasterId: 0,
      sspiQuestionDetail: "",
      sspiQuestionIsActive: true,
      sspiCorrectAnswerOption: 1,
      sspiOptions: [{
        sspiOption1: "",
        sspiOption2: "",
        sspiOption3: "",
        sspiOption4: ""
      }]
    },
    searchQuery: "",
    filterStatus: "all",
    currentPage: 0,
    itemsPerPage: 5
  });

  // Helper function to update state
  const updateState = (newState) => {
    setState(prev => ({ ...prev, ...newState }));
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchSSPIQuestions();
    fetchSSPIMasters();
    fetchSSPIExams();
    fetchClassList();
  }, []);

  const fetchSSPIExams = async () => {
    try {
      const response = await axios.get(`${API_LoginURL}Sspi/SspiExamList`, {
        headers: getHeaders(),
      });
      updateState({ sspiExam: Array.isArray(response.data) ? response.data : [] });
    } catch (error) {
      console.error("Error fetching SSPI Exams:", error);
      Swal.fire("Error!", "Failed to load SSPI Exams.", "error");
    }
  };

  const fetchClassList = async () => {
    try {
      const response = await axios.get(`${API_LoginURL}Class/ClassMasterList`, {
        headers: getHeaders(),
      });
      updateState({ classList: Array.isArray(response.data) ? response.data : [] });
    } catch (error) {
      console.error("Error fetching SSPI Classes:", error);
      Swal.fire("Error!", "Failed to load SSPI Classes.", "error");
    }
  };

  // API calls
  const fetchSSPIMasters = async () => {
    try {
      const response = await axios.get(`${API_LoginURL}Sspi/SspiMasterList`, {
        headers: getHeaders(),
      });
      updateState({ sspiMasters: Array.isArray(response.data) ? response.data : [] });
    } catch (error) {
      console.error("Error fetching SSPI Masters:", error);
      Swal.fire("Error!", "Failed to load SSPI Masters.", "error");
    }
  };

  const fetchSSPIQuestions = async () => {
    updateState({ loading: true });
    try {
      const response = await axios.get(`${API_LoginURL}Sspi/SspiQuestionList`, {
        headers: getHeaders(),
      });
      updateState({
        sspiQuestions: Array.isArray(response.data) ? response.data : [],
        loading: false
      });
    } catch (error) {
      console.error("Error fetching SSPI Questions:", error);
      Swal.fire("Error!", "Failed to load SSPI Questions.", "error");
      updateState({ loading: false });
    }
  };

  // Helper function for headers
  const getHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: localStorage.getItem("token")
  });

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    updateState({
      formData: {
        ...state.formData,
        [name]: value
      }
    });
  };

  const handleOptionChange = (e) => {
    const { name, value } = e.target;
    updateState({
      formData: {
        ...state.formData,
        sspiOptions: [{
          ...state.formData.sspiOptions[0],
          [name]: value
        }]
      }
    });
  };

  const handleSelectChange = (selectedOption) => {
    updateState({
      formData: {
        ...state.formData,
        sspiMasterId: selectedOption ? selectedOption.value : 0
      }
    });
  };

  // Debounced search
  const debouncedSearch = useMemo(
    () => debounce((query) => {
      updateState({ searchQuery: query, currentPage: 0 });
    }, 300),
    []
  );

  const handleSearchChange = (e) => {
    debouncedSearch(e.target.value);
  };

  const handleFilterChange = (e) => {
    updateState({
      filterStatus: e.target.value,
      currentPage: 0
    });
  };

  const resetForm = () => {
    updateState({
      formData: {
        sspiQuestionId: 0,
        sspiMasterId: 0,
        sspiQuestionDetail: "",
        sspiQuestionIsActive: true,
        sspiCorrectAnswerOption: 1,
        sspiOptions: [{
          sspiOption1: "",
          sspiOption2: "",
          sspiOption3: "",
          sspiOption4: ""
        }]
      }
    });
  };

  const handleEdit = (question) => {
    updateState({ 
      formData: {
        ...question,
        sspiQuestionIsActive: question.sspiQuestionIsActive !== false,
        sspiOptions: question.sspiOptions || [{
          sspiOption1: "",
          sspiOption2: "",
          sspiOption3: "",
          sspiOption4: ""
        }]
      }
    });
  };

  const handleToggleActive = async (sspiQuestionId, sspiQuestionIsActive) => {
    try {
      await axios.post(
        `${API_LoginURL}Sspi/ToggleSspiQuestionActive`,
        { sspiQuestionId, sspiQuestionIsActive: !sspiQuestionIsActive },
        { headers: getHeaders() }
      );
      fetchSSPIQuestions();
      Swal.fire("Success!", "Status updated successfully.", "success");
    } catch (error) {
      console.error("Error toggling SSPI Question status:", error);
      Swal.fire("Error", "Failed to update status", "error");
    }
  };

  const handleDelete = async (sspiQuestionId) => {
    const confirmation = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    });

    if (!confirmation.isConfirmed) return;

    try {
      await axios.post(
        `${API_LoginURL}Sspi/DeleteSspiQuestion`,
        { sspiQuestionId },
        { headers: getHeaders() }
      );
      updateState({
        sspiQuestions: state.sspiQuestions.filter(item => item.sspiQuestionId !== sspiQuestionId)
      });
      Swal.fire("Deleted!", "SSPI Question has been deleted.", "success");
    } catch (error) {
      console.error("Error deleting SSPI Question:", error);
      Swal.fire("Error", "Failed to delete SSPI Question", "error");
    }
  };

  const handlePageChange = ({ selected }) => {
    updateState({ currentPage: selected });
  };

  const handleFormSubmit = async () => {
    updateState({ formSubmitting: true });
    try {
      const formData = new FormData();
      
      // Append all fields to formData
      // formData.append('SspiQuestionId', state.formData.sspiQuestionId);
      formData.append('SspiQuestionDetail', state.formData.sspiQuestionDetail);
      formData.append('SspiMasterId', state.formData.sspiMasterId);
      formData.append('SspiOption1', state.formData.sspiOptions[0].sspiOption1);
      formData.append('SspiOption2', state.formData.sspiOptions[0].sspiOption2);
      formData.append('SspiOption3', state.formData.sspiOptions[0].sspiOption3);
      formData.append('SspiOption4', state.formData.sspiOptions[0].sspiOption4);
      formData.append('SspiCorrectAnswerOption', state.formData.sspiCorrectAnswerOption);

      const response = await axios.post(
        `${API_LoginURL}Sspi/SspiQuestionAddEdit`,
        formData,
        { 
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: localStorage.getItem("token")
          }
        }
      );

      Swal.fire({
        icon: response.data.response === "SUCCESS" ? "success" : "error",
        title: response.data.response === "SUCCESS" ? "Success" : "Error",
        text: response.data.response === "SUCCESS" 
          ? "SSPI Question saved successfully" 
          : response.data.response,
      });

      if (response.data.response === "SUCCESS") {
        fetchSSPIQuestions();
        document.getElementById("closeSSPIQuestionModal").click();
        resetForm();
      }
    } catch (error) {
      console.error("Error saving SSPI Question:", error);
      Swal.fire("Error", "Failed to save SSPI Question", "error");
    } finally {
      updateState({ formSubmitting: false });
    }
  };

  // Filter and pagination
  const filteredSSPIQuestions = state.sspiQuestions.filter(item => {
    const matchesSearch = 
      item.sspiQuestionDetail.toLowerCase().includes(state.searchQuery.toLowerCase()) ||
      (item.sspiMasterName && item.sspiMasterName.toLowerCase().includes(state.searchQuery.toLowerCase()));
    
    const matchesStatus = 
      state.filterStatus === "all" ||
      (state.filterStatus === "active" && item.sspiQuestionIsActive) ||
      (state.filterStatus === "inactive" && !item.sspiQuestionIsActive);
    
    return matchesSearch && matchesStatus;
  });

  const pageCount = Math.ceil(filteredSSPIQuestions.length / state.itemsPerPage);
  const displayedSSPIQuestions = filteredSSPIQuestions.slice(
    state.currentPage * state.itemsPerPage,
    (state.currentPage + 1) * state.itemsPerPage
  );

  // Get display name for SSPI Master
  const getSSPIMasterName = (sspiMasterId) => {
    const master = state.sspiMasters.find(m => m.sspiMasterId === sspiMasterId);
    const exam = state.sspiExam.find(e => e.sspiExamId === master?.sspiExamId);
    const className = state.classList.find(c => c.classId === master?.classId);
    return master ? `${exam?.sspiExamName} - ${className?.className}` : "N/A";
  };

  return (
    <div className="container-scroller">
      <Navbar />
      <div className="container-fluid page-body-wrapper">
        <SASidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="col-lg-12 grid-margin stretch-card">
              <div className="card">
                <div className="card-body">
                  {/* Header Section */}
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4">
                    <h4 className="card-title mb-3 mb-md-0">SSPI Question Management</h4>
                    
                    {/* Search and Filter */}
                    <div className="d-flex flex-column flex-md-row align-items-center gap-3 w-100 w-md-auto">
                      {/* Search */}
                      <div
                        className="flex-grow-1 me-md-2 position-relative"
                        style={{ maxWidth: "500px" }}
                      >
                        <input
                          type="text"
                          className="form-control form-control-sm ps-5 rounded-pill border-0"
                          placeholder="Search by Question or SSPI Master"
                          onChange={handleSearchChange}
                          style={{
                            height: "38px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                            transition: "all 0.3s ease",
                            backgroundColor: "#f8f9fa",
                          }}
                          onFocus={(e) => {
                            e.target.style.boxShadow = "0 2px 12px rgba(0,0,0,0.15)";
                            e.target.style.backgroundColor = "#fff";
                          }}
                          onBlur={(e) => {
                            e.target.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
                            e.target.style.backgroundColor = "#f8f9fa";
                          }}
                        />
                        <i className="fas fa-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                      </div>
                      
                      {/* Filter */}
                      <div className="d-flex align-items-center">
                        <i className="fas fa-filter me-2 text-muted"></i>
                        <select
                          className="form-select"
                          value={state.filterStatus}
                          onChange={handleFilterChange}
                          style={{ minWidth: '120px' }}
                        >
                          <option value="all">All Status</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                      
                      {/* Add Question Button */}
                      <button
                        className="btn btn-primary d-flex align-items-center"
                        data-bs-toggle="modal"
                        data-bs-target="#sspiQuestionModal"
                        onClick={resetForm}
                      >
                        <i className="fas fa-plus me-2"></i>
                        <span className="d-none d-md-inline">Add Question</span>
                      </button>
                    </div>
                  </div>

                  {/* SSPI Question Table */}
                  <div className="table-responsive">
                    {state.loading ? (
                      <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                        <ClipLoader color="#4e73df" size={50} />
                        <p className="mt-3 ms-3">Loading SSPI Questions...</p>
                      </div>
                    ) : (
                      <>
                        <table className="table table-hover table-striped">
                          <thead className="table-primary">
                            <tr>
                              <th width="5%">ID</th>
                              <th width="20%">SSPI Master</th>
                              <th width="30%">Question</th>
                              <th width="15%">Correct Answer</th>
                              <th width="10%">Attempted</th>
                              <th width="10%">Status</th>
                              <th width="10%" className="text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayedSSPIQuestions.length > 0 ? (
                              displayedSSPIQuestions.map((item) => (
                                <tr key={item.sspiQuestionId}>
                                  <td>{item.sspiQuestionId}</td>
                                  <td>{getSSPIMasterName(item.sspiMasterId)}</td>
                                  <td>{item.sspiQuestionDetail}</td>
                                  <td>
                                    {item.sspiOptions && item.sspiOptions[0]?.[`sspiOption${item.sspiCorrectAnswerOption}`]}
                                  </td>
                                  <td>{item.attempted || 0}</td>
                                  <td>
                                    <div className="form-check form-switch">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={item.sspiQuestionIsActive}
                                        onChange={() => handleToggleActive(item.sspiQuestionId,item.sspiQuestionIsActive)}
                                        id={`toggle-${item.sspiQuestionId}`}
                                      />
                                      <label 
                                        className="form-check-label" 
                                        htmlFor={`toggle-${item.sspiQuestionId}`}
                                        style={{ color: item.sspiQuestionIsActive==true ? "green" : "red" }}
                                      >
                                        {item.sspiQuestionIsActive==true ? "Active" : "Inactive"}
                                      </label>
                                    </div>
                                  </td>
                                  <td className="text-end">
                                    <div className="d-flex justify-content-end gap-2">
                                      <button
                                        className="btn btn-sm btn-outline-primary"
                                        data-bs-toggle="modal"
                                        data-bs-target="#sspiQuestionModal"
                                        onClick={() => handleEdit(item)}
                                        title="Edit"
                                      >
                                        <i className="fas fa-edit"></i>
                                      </button>
                                      <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => handleDelete(item.sspiQuestionId)}
                                        title="Delete"
                                      >
                                        <i className="fas fa-trash"></i>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="7" className="text-center py-4">
                                  <div className="d-flex flex-column align-items-center">
                                    <img
                                      src="https://img.icons8.com/ios/50/000000/no-camera--v1.png"
                                      alt="No questions"
                                      style={{ opacity: 0.5, width: '60px' }}
                                    />
                                    <p className="mt-3 text-muted">No SSPI Questions found</p>
                                    <button
                                      className="btn btn-sm btn-primary mt-2"
                                      data-bs-toggle="modal"
                                      data-bs-target="#sspiQuestionModal"
                                      onClick={resetForm}
                                    >
                                      Add New Question
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>

                        {/* Pagination */}
                        {displayedSSPIQuestions.length > 0 && (
                          <ReactPaginate
                            previousLabel={<i className="fas fa-chevron-left"></i>}
                            nextLabel={<i className="fas fa-chevron-right"></i>}
                            pageCount={pageCount}
                            onPageChange={handlePageChange}
                            containerClassName={"pagination justify-content-center mt-4"}
                            pageClassName={"page-item"}
                            pageLinkClassName={"page-link"}
                            previousClassName={"page-item"}
                            previousLinkClassName={"page-link"}
                            nextClassName={"page-item"}
                            nextLinkClassName={"page-link"}
                            activeClassName={"active"}
                            breakLabel={"..."}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={5}
                            forcePage={state.currentPage}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SSPI Question Modal */}
      <div className="modal fade" id="sspiQuestionModal" tabIndex="-1" aria-labelledby="sspiQuestionModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header bg-light">
              <h5 className="modal-title" id="sspiQuestionModalLabel">
                {state.formData.sspiQuestionId > 0 ? 'Edit SSPI Question' : 'Add New SSPI Question'}
              </h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body">
              <form>
                <div className="row">
                <div className="mb-3 col-md-6">
                  <label htmlFor="sspiMasterId" className="form-label required">SSPI Master</label>
                  <Select
                    id="sspiMasterId"
                    options={state.sspiMasters.map(master => {
                      const exam = state.sspiExam.find(e => e.sspiExamId === master?.sspiExamId);
                      const className = state.classList.find(c => c.classId === master?.classId);
                      return {
                        value: master.sspiMasterId,
                        label: `${exam?.sspiExamName} - ${className?.className}`
                      };
                    })}
                    value={state.sspiMasters.map(master => {
                      const exam = state.sspiExam.find(e => e.sspiExamId === master?.sspiExamId);
                      const className = state.classList.find(c => c.classId === master?.classId);
                      return {
                        value: master.sspiMasterId,
                        label: `${exam?.sspiExamName} - ${className?.className}`
                      };
                    }).find(option => option.value === state.formData.sspiMasterId) || null}
                    onChange={handleSelectChange}
                    placeholder="Select SSPI Master"
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable
                    isSearchable
                  />
                </div>
                </div>
                <div className="row">
                <div className="mb-3">
                  <label htmlFor="sspiQuestionDetail" className="form-label required">Question</label>
                  <textarea
                    className="form-control"
                    id="sspiQuestionDetail"
                    name="sspiQuestionDetail"
                    rows="2"
                    value={state.formData.sspiQuestionDetail}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                </div>
                
                <div className="row">
                <div className="mb-3">
                  <label className="form-label required">Options</label>
                  <div className="row">
                    <div className="col-md-6 mb-2">
                      <div className="input-group">
                        <div className="input-group-text">
                          <input
                            type="radio"
                            name="sspiCorrectAnswerOption"
                            value="1"
                            checked={state.formData.sspiCorrectAnswerOption == 1}
                            onChange={handleInputChange}
                          />
                        </div>
                        <input
                          type="text"
                          className="form-control"
                          name="sspiOption1"
                          placeholder="Option 1"
                          value={state.formData.sspiOptions[0].sspiOption1}
                          onChange={handleOptionChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6 mb-2">
                      <div className="input-group">
                        <div className="input-group-text">
                          <input
                            type="radio"
                            name="sspiCorrectAnswerOption"
                            value="2"
                            checked={state.formData.sspiCorrectAnswerOption == 2}
                            onChange={handleInputChange}
                          />
                        </div>
                        <input
                          type="text"
                          className="form-control"
                          name="sspiOption2"
                          placeholder="Option 2"
                          value={state.formData.sspiOptions[0].sspiOption2}
                          onChange={handleOptionChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6 mb-2">
                      <div className="input-group">
                        <div className="input-group-text">
                          <input
                            type="radio"
                            name="sspiCorrectAnswerOption"
                            value="3"
                            checked={state.formData.sspiCorrectAnswerOption == 3}
                            onChange={handleInputChange}
                          />
                        </div>
                        <input
                          type="text"
                          className="form-control"
                          name="sspiOption3"
                          placeholder="Option 3"
                          value={state.formData.sspiOptions[0].sspiOption3}
                          onChange={handleOptionChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6 mb-2">
                      <div className="input-group">
                        <div className="input-group-text">
                          <input
                            type="radio"
                            name="sspiCorrectAnswerOption"
                            value="4"
                            checked={state.formData.sspiCorrectAnswerOption == 4}
                            onChange={handleInputChange}
                          />
                        </div>
                        <input
                          type="text"
                          className="form-control"
                          name="sspiOption4"
                          placeholder="Option 4"
                          value={state.formData.sspiOptions[0].sspiOption4}
                          onChange={handleOptionChange}
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button type="button" id="closeSSPIQuestionModal" className="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleFormSubmit}
                disabled={state.formSubmitting}
              >
                {state.formSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Submitting...
                  </>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}